import type {
  TipProgressEntry,
  UiProgress,
  WalkthroughProgressEntry,
} from './types';

export const emptyUiProgress = (): UiProgress => ({
  tips: {},
  walkthroughs: {},
});

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const asString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const asVersion = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.max(0, Math.floor(value));
};

const normalizeTipEntry = (raw: unknown): TipProgressEntry | null => {
  if (!isPlainObject(raw)) return null;
  const entry: TipProgressEntry = {};
  const dismissed = asString(raw.dismissed_at);
  const completed = asString(raw.completed_at);
  const version = asVersion(raw.version);
  if (dismissed) entry.dismissed_at = dismissed;
  if (completed) entry.completed_at = completed;
  if (version !== undefined) entry.version = version;
  return Object.keys(entry).length ? entry : null;
};

const normalizeWalkthroughEntry = (
  raw: unknown
): WalkthroughProgressEntry | null => {
  if (!isPlainObject(raw)) return null;
  const entry: WalkthroughProgressEntry = {};
  const step = asString(raw.step);
  const completed = asString(raw.completed_at);
  const skipped = asString(raw.skipped_at);
  const version = asVersion(raw.version);
  if (step) entry.step = step;
  if (completed) entry.completed_at = completed;
  if (skipped) entry.skipped_at = skipped;
  if (version !== undefined) entry.version = version;
  return Object.keys(entry).length ? entry : null;
};

export const normalizeUiProgress = (raw: unknown): UiProgress => {
  const out = emptyUiProgress();
  if (!isPlainObject(raw)) return out;

  if (isPlainObject(raw.tips)) {
    for (const [key, value] of Object.entries(raw.tips)) {
      if (!key) continue;
      const entry = normalizeTipEntry(value);
      if (entry) out.tips[key] = entry;
    }
  }

  if (isPlainObject(raw.walkthroughs)) {
    for (const [key, value] of Object.entries(raw.walkthroughs)) {
      if (!key) continue;
      const entry = normalizeWalkthroughEntry(value);
      if (entry) out.walkthroughs[key] = entry;
    }
  }

  return out;
};

/**
 * Deep-merge tip/walkthrough keys. Null values delete a key (replay reset).
 */
export const mergeUiProgress = (
  current: unknown,
  patch: unknown
): UiProgress => {
  const base = normalizeUiProgress(current);
  if (!isPlainObject(patch)) return base;

  if (isPlainObject(patch.tips)) {
    for (const [key, value] of Object.entries(patch.tips)) {
      if (!key) continue;
      if (value === null) {
        delete base.tips[key];
        continue;
      }
      const entry = normalizeTipEntry(value);
      if (entry) base.tips[key] = entry;
    }
  }

  if (isPlainObject(patch.walkthroughs)) {
    for (const [key, value] of Object.entries(patch.walkthroughs)) {
      if (!key) continue;
      if (value === null) {
        delete base.walkthroughs[key];
        continue;
      }
      const entry = normalizeWalkthroughEntry(value);
      if (entry) base.walkthroughs[key] = entry;
    }
  }

  return base;
};

export const isTourComplete = (
  progress: UiProgress,
  tourId: string,
  version: number
): boolean => {
  const entry = progress.walkthroughs[tourId];
  if (!entry) return false;
  if (entry.version !== undefined && entry.version < version) return false;
  return Boolean(entry.completed_at || entry.skipped_at);
};

export const isTipDone = (
  progress: UiProgress,
  tipId: string,
  version: number
): boolean => {
  const entry = progress.tips[tipId];
  if (!entry) return false;
  if (entry.version !== undefined && entry.version < version) return false;
  return Boolean(entry.completed_at || entry.dismissed_at);
};
